// いくつかの画面で使う部品：札・一行の説明・タイル・広告の枠。
import { ads } from "../ads";
import type { CatalogGame } from "../catalog";
import { localePath, pick, t, type MessageKey } from "../i18n";
import { h } from "./dom";
import { icon } from "./icons";

export function timeLabel(g: CatalogGame): string {
  const [a, b] = g.minutes;
  return a === b ? t("time.about", { n: a }) : t("time.range", { a, b });
}

/** 札：教科と学年（学びのゲーム）か種類、時間、下書き */
export function tags(g: CatalogGame): HTMLElement {
  return h(
    "span",
    { class: "tags" },
    g.subject
      ? h("span", { class: `tg s-${g.subject}` }, t(`subject.${g.subject}` as MessageKey))
      : h("span", { class: "tg genre" }, t(`genre.${g.genre}` as MessageKey)),
    g.grade && h("span", { class: "tg grade" }, t(`grade.${g.grade}` as MessageKey)),
    h("span", { class: "tg time" }, icon("clock"), h("span", { class: "nw" }, timeLabel(g))),
    g.length === "long" && h("span", { class: "tg long" }, t("badge.long")),
    g.status === "draft" && h("span", { class: "tg draft" }, t("badge.draft")),
  );
}

export const gamePath = (g: CatalogGame) => localePath(`/games/${g.id}/`);

/** 一行の説明：種類（学びのゲームは教科と学年）・時間・続きから遊べるか。札にせず文字で出す */
export function metaLine(g: CatalogGame): HTMLElement {
  const kind = g.subject
    ? [t(`subject.${g.subject}` as MessageKey), g.grade ? t(`grade.${g.grade}` as MessageKey) : ""].filter(Boolean).join(" ")
    : t(`genre.${g.genre}` as MessageKey);
  return h(
    "span",
    { class: "meta" },
    kind,
    "・",
    h("span", { class: "nw" }, timeLabel(g)),
    g.length === "long" && "・",
    g.length === "long" && h("span", { class: "long" }, t("badge.long")),
    g.status === "draft" && "・",
    g.status === "draft" && t("badge.draft"),
  );
}

/** ゲームのタイル（CrazyGames の形）：正方形の絵だけ。PC で指を乗せると、ピンクの縁で手前に出て大きくなり、
 *  遊んでいる様子の動画が流れ、題名と一行の説明の白い札が出る。
 *  画面に出していなくても題名は札の中に持つ（読み上げと検索のため）。testid は今日のおすすめのタイルだけに付ける（home.ts） */
export function gameTile(g: CatalogGame, opts: { testid?: string; eager?: boolean; extra?: Node | false } = {}): HTMLElement {
  return hoverPreview(
    h(
      "a",
      { class: "gt", href: gamePath(g), ...(opts.testid ? { "data-testid": opts.testid } : {}) },
      h("img", { src: g.thumbnail, alt: "", width: 512, height: 512, loading: opts.eager ? "eager" : "lazy", decoding: "async" }),
      h("span", { class: "gt-ov" }, h("span", { class: "gt-t ph" }, pick(g.title)), metaLine(g)),
      opts.extra,
    ),
    g,
  );
}

const canHover = matchMedia("(hover: hover) and (pointer: fine)");
const reduceMotion = matchMedia("(prefers-reduced-motion: reduce)");

/** 指（マウス）を乗せている間だけ、遊んでいる様子の動画（音なし・くり返し）を絵の上に流す。
 *  すぐ通り過ぎただけでは読まない。動きを減らす設定・データを節約する設定のときと、動画の無いゲームでは何もしない */
export function hoverPreview<T extends HTMLElement>(tile: T, g: CatalogGame): T {
  if (!g.preview) return tile;
  let video: HTMLVideoElement | null = null;
  let timer = 0;
  const stop = () => {
    window.clearTimeout(timer);
    if (!video) return;
    const v = video;
    video = null;
    v.classList.remove("on");
    v.pause();
    window.setTimeout(() => v.remove(), 250);
  };
  tile.addEventListener("pointerenter", (e) => {
    const saveData = (navigator as Navigator & { connection?: { saveData?: boolean } }).connection?.saveData;
    if (e.pointerType !== "mouse" || !canHover.matches || reduceMotion.matches || saveData) return;
    timer = window.setTimeout(() => {
      const v = h("video", { class: "gt-vid", src: g.preview!, muted: true, loop: true, playsinline: true, preload: "auto", "aria-hidden": "true" });
      v.muted = true;
      v.addEventListener("playing", () => v.classList.add("on"), { once: true });
      tile.querySelector(":scope > img")?.after(v);
      video = v;
      v.play().catch(() => {});
    }, 120);
  });
  tile.addEventListener("pointerleave", stop);
  return tile;
}

/** 広告の枠。見本のあいだは「広告の枠（見本）」と出る */
export function adSlot(slot: "home" | "play", extraClass = ""): HTMLElement {
  const el = h("aside", { class: `ad-slot ${extraClass}`.trim(), "aria-label": t("ads.label") });
  ads.mountBanner(el, slot);
  return el;
}
